import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Breadcrumbs, Button, Link as MuiLink, Paper, Skeleton, Stack, Tab, Tabs, Typography } from '@mui/material';
import { ArrowBack, MailOutlined, PersonSearchOutlined } from '@mui/icons-material';
import { Link as RouterLink, useParams, useSearchParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import { studentProfileService, StudentProfileResponse } from '../../services/studentProfileService';
import { useModulePermissions } from '../../hooks/useModulePermissions';
import { useCurrencyFormat } from '../../hooks/useCurrencyFormat';
import EmptyState from '../../components/common/EmptyState';
import RecordFeePaymentDialog from '../../components/fees/RecordFeePaymentDialog';
import StudentLettersTab from '../../components/letters/StudentLettersTab';
import ProfileHeader from '../../components/students/profile/ProfileHeader';
import StudentOverviewTab from '../../components/students/profile/StudentOverviewTab';
import StudentAcademicsTab from '../../components/students/profile/StudentAcademicsTab';
import StudentFeesLifetimeTab from '../../components/students/profile/StudentFeesLifetimeTab';
import StudentTimelineTab from '../../components/students/profile/StudentTimelineTab';
import { buildProfileEvents } from '../../components/students/profile/profileEvents';
import { PROFILE_TABS, ProfileTab, profileTabFrom } from '../../components/students/profile/profileNav';

/** Student 360 — everything about one person, found by GR number (or by
 *  id for an applicant, who has no GR number until enrollment). */
const StudentProfilePage: React.FC = () => {
  const { ref = '' } = useParams<{ ref: string }>();
  const { enqueueSnackbar } = useSnackbar();
  const { canWrite } = useModulePermissions();
  const [searchParams, setSearchParams] = useSearchParams();

  const [data, setData] = useState<StudentProfileResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [collecting, setCollecting] = useState<false | 'fees' | 'advance'>(false);

  const activeTab = profileTabFrom(searchParams.get('tab'));

  // First load shows skeletons; later refreshes keep the page in place.
  const load = useCallback(async () => {
    setNotFound(false);
    try {
      setData(await studentProfileService.get(ref));
    } catch (err: any) {
      if (err?.status === 404 || /no record/i.test(err?.message || '')) setNotFound(true);
      else enqueueSnackbar(err?.message || 'Could not load this profile', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [ref, enqueueSnackbar]);

  useEffect(() => { setLoading(true); setData(null); load(); }, [load]);

  const money = useCurrencyFormat();
  const events = useMemo(() => (data ? buildProfileEvents(data, money) : []), [data, money]);

  const openTab = useCallback((tab: ProfileTab) => {
    const sp = new URLSearchParams(searchParams);
    sp.set('tab', tab);
    setSearchParams(sp, { replace: true });
  }, [searchParams, setSearchParams]);

  const backToLookup = (
    <MuiLink component={RouterLink} to="/app/students/lookup" underline="hover" color="text.secondary" variant="body2">
      Student lookup
    </MuiLink>
  );

  if (loading) {
    return (
      <Stack spacing={2.5}>
        <Skeleton variant="text" width={220} />
        <Skeleton variant="rounded" height={220} sx={{ borderRadius: 3 }} />
        <Skeleton variant="rounded" height={360} sx={{ borderRadius: 3 }} />
      </Stack>
    );
  }

  if (notFound || !data) {
    return (
      <Paper variant="outlined" sx={{ borderRadius: 3, py: 4 }}>
        <EmptyState
          icon={<PersonSearchOutlined />}
          title={/^\d+$/.test(ref) ? `No one with GR ${ref}` : 'No one found'}
          description="Check the number, or search by name, CNIC or phone."
        />
        <Stack alignItems="center">
          <Button component={RouterLink} to="/app/students/lookup" startIcon={<ArrowBack />} sx={{ textTransform: 'none' }}>
            Back to lookup
          </Button>
        </Stack>
      </Paper>
    );
  }

  const { person, student, admissions, enrollments, fees } = data;

  return (
    <Stack spacing={2.5}>
      <Breadcrumbs>
        {backToLookup}
        <Typography color="text.primary" variant="body2">{person.name}</Typography>
      </Breadcrumbs>

      <ProfileHeader
        data={data}
        canEditPhoto={canWrite('enrollments')}
        canCollect={canWrite('fees')}
        onCollect={advance => setCollecting(advance ? 'advance' : 'fees')}
        onOpenTab={openTab}
        onChanged={load}
      />

      <Box>
        <Tabs
          value={activeTab}
          onChange={(_, v: ProfileTab) => openTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mb: 2.5, minHeight: 40, borderBottom: 1, borderColor: 'divider' }}
        >
          {PROFILE_TABS.map(t => (
            <Tab key={t.key} value={t.key} label={t.label} sx={{ textTransform: 'none', minHeight: 40, fontWeight: 600 }} />
          ))}
        </Tabs>

        {activeTab === 'overview' && <StudentOverviewTab data={data} events={events} onOpenTab={openTab} />}
        {activeTab === 'academics' && <StudentAcademicsTab admissions={admissions} enrollments={enrollments} onChanged={load} />}
        {activeTab === 'fees' && <StudentFeesLifetimeTab fees={fees} onChanged={load} />}
        {activeTab === 'letters' && (student
          ? <StudentLettersTab studentId={student._id} studentName={person.name} onChanged={load} />
          : <EmptyState icon={<MailOutlined />} title="No letters" description="Letters can be issued once the applicant is enrolled." dense />)}
        {activeTab === 'timeline' && <StudentTimelineTab events={events} />}
      </Box>

      <RecordFeePaymentDialog
        open={!!collecting}
        advance={collecting === 'advance'}
        personId={person._id}
        onClose={() => setCollecting(false)}
        onRecorded={() => load()}
      />
    </Stack>
  );
};

export default StudentProfilePage;
